/**
 * Feature flags the server turns on per player (the server's FeatureService:
 * a kill switch, portal toggles, beta codes, test accounts and a percent
 * rollout). They arrive as {@code Player.features} on the player fetch, with
 * {@code Player.featureThemes}: the slots each flag that runs on some slots
 * only (Royal Road, and the Wheel Run on the slots without a road) runs on
 * for the player. The app fetches the player again
 * when the tab comes back and when a slot opens, so a world switched on or
 * off on the server reaches open clients.
 */
import { ROAD_THEMES_DEFAULT } from '@goodtime/shared';

export type FeatureName =
  | 'royal_road'
  | 'wheel_run'
  | 'road_story'
  | 'realm_map'
  | 'realm_alive'
  | 'world_maps'
  | 'overview_strip'
  | 'accounts'
  | 'reel_collect'
  | 'reel_gems';

/** Every flag this build knows. Names the server sends that aren't here are
 *  dropped, so an older build ignores a newer feature. */
export const KNOWN_FEATURES: readonly FeatureName[] = [
  'royal_road',
  'wheel_run',
  'road_story',
  'realm_map',
  'realm_alive',
  'world_maps',
  'overview_strip',
  'accounts',
  'reel_collect',
  'reel_gems',
];

function isKnown(name: string): name is FeatureName {
  return (KNOWN_FEATURES as readonly string[]).includes(name);
}

/** The player's features from the server response. Anything malformed or
 *  missing (an older server) reads as no features. */
export function parseFeatures(raw: unknown): Set<FeatureName> {
  const out = new Set<FeatureName>();
  if (!Array.isArray(raw)) return out;
  for (const name of raw) {
    if (typeof name === 'string' && isKnown(name)) out.add(name);
  }
  return out;
}

/** For each feature that runs on some slots only, the slots it runs on for
 *  the player. An empty list means no slot. */
export type FeatureThemes = Partial<Record<FeatureName, readonly string[]>>;

/** A slot id as the server writes one ("forest", "sherbet_square"). */
const SLUG = /^[a-z0-9_]+$/;

/**
 * The player's per-feature slots from the server response, or null when the
 * server sent none (one from before {@code featureThemes}). Unknown features
 * are dropped; a known feature's list keeps only slot ids, and anything else
 * in its place reads as no slots, so a bad answer turns a road off rather
 * than on.
 */
export function parseFeatureThemes(raw: unknown): FeatureThemes | null {
  if (raw === undefined || raw === null) return null;
  const out: FeatureThemes = {};
  if (typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const [name, slots] of Object.entries(raw)) {
    if (!isKnown(name)) continue;
    out[name] = Array.isArray(slots) ? slots.filter((s): s is string => typeof s === 'string' && SLUG.test(s)) : [];
  }
  return out;
}

/** The slots a feature runs on when the server doesn't say: Royal Road's
 *  first two roads, as every server before per-player lists ran it. The
 *  Wheel Run came after the lists, so without one it runs nowhere. */
const SLOTS_BEFORE_LISTS: FeatureThemes = { royal_road: ROAD_THEMES_DEFAULT };

/**
 * Whether a feature that runs on some slots only is on for the player on
 * this slot: the feature is on ({@code on}) and the server's list for it has
 * the slot. Without a list (an older server, or a feature turned on by the
 * DEV override) the slots it ran on before lists apply.
 */
export function onSlot(
  on: ReadonlySet<FeatureName>,
  themes: FeatureThemes | null,
  name: FeatureName,
  themeId: string,
): boolean {
  if (!on.has(name)) return false;
  const slots = themes?.[name] ?? SLOTS_BEFORE_LISTS[name] ?? [];
  return slots.includes(themeId);
}

/**
 * The DEV-only {@code ?ff=} override, for trying a feature without the
 * portal: {@code ?ff=royal_road,realm_map} turns those on, and
 * {@code ?ff=-royal_road} turns one off. Ignored outside dev builds.
 */
export function applyOverride(
  features: Set<FeatureName>,
  search: string,
  dev: boolean,
): Set<FeatureName> {
  if (!dev) return features;
  const param = new URLSearchParams(search).get('ff');
  if (!param) return features;
  const out = new Set(features);
  for (const part of param.split(',')) {
    const name = part.trim();
    if (name.startsWith('-')) {
      const off = name.slice(1);
      if (isKnown(off)) out.delete(off);
    } else if (isKnown(name)) {
      out.add(name);
    }
  }
  return out;
}
